import { useEffect, useMemo, useRef, useState } from 'react'
/* eslint-disable @typescript-eslint/no-deprecated -- lucide deprecated its
   brand icons; the whole app still renders <Youtube /> for YouTube
   sources, so swapping it here alone would look inconsistent. */
import { Check, Download, Film, Loader2, Trash2, Upload, Youtube } from 'lucide-react'
import { useJob } from '@/api/endpoints/jobs'
import {
  readTranscodeSnapshot,
  useDeleteImport,
  useImports,
  useInvalidateImports,
  useUploadForCut,
  type ImportedItem,
  type UploadedForCut,
  type UploadForCutProgress,
} from '@/api/endpoints/videoCutter'
import {
  readDownloadSnapshot,
  useStartYoutubeDownload,
  type YoutubeDownloadSnapshot,
} from '@/api/endpoints/youtubeDownload'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { formatBytes, relativeTime } from '../format'
import { newId, type ProjectVideo } from '../model'
import { UploadProgressCard } from './UploadProgressCard'
import styles from '../styles/shell.module.scss'

const ACCEPTED_MIME = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska']
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024 * 1024 // 4 GB
const YOUTUBE_URL_RE = /^https?:\/\/(?:www\.|m\.)?(?:youtube\.com|youtu\.be)\//i

type Tab = 'upload' | 'youtube' | 'existing'

function fromUpload(u: UploadedForCut): ProjectVideo {
  return {
    id: newId('vid'),
    tempPath: u.temp_path,
    videoUrl: u.video_url,
    proxyUrl: u.proxy_url,
    filename: u.filename,
    origin: 'upload',
    duration: u.duration,
    width: u.width ?? null,
    height: u.height ?? null,
    fileSize: u.file_size ?? null,
  }
}

function fromYoutube(snap: YoutubeDownloadSnapshot): ProjectVideo | null {
  if (!snap.download_url || !snap.temp_path) return null
  return {
    id: newId('vid'),
    tempPath: snap.temp_path,
    videoUrl: snap.download_url,
    // YouTube downloads don't go through the prepare job yet.
    proxyUrl: null,
    filename: snap.suggested_filename ?? snap.stats?.title ?? 'video.mp4',
    origin: 'youtube',
    duration: snap.stats?.duration ?? 0,
    width: null,
    height: null,
    fileSize: snap.stats?.file_size ?? null,
  }
}

export function fromImport(item: ImportedItem): ProjectVideo {
  return {
    id: newId('vid'),
    tempPath: item.temp_path,
    videoUrl: item.video_url,
    proxyUrl: item.proxy_url,
    filename: item.filename,
    // Duration comes from the browser on ``loadedmetadata`` — the list
    // endpoint doesn't probe (too expensive for a long list).
    duration: 0,
    origin: item.kind === 'upload' ? 'upload' : 'youtube',
    width: null,
    height: null,
    fileSize: item.size_bytes,
  }
}

export interface VideoImporterProps {
  /** Called once per successfully imported video. */
  onVideo: (video: ProjectVideo) => void
  /** Paths already attached to the current project — listed as "já no
   * projeto" instead of being offered again. */
  usedTempPaths?: string[]
  /** Show the "already on the server" tab. Off on the projects screen,
   * where every import starts a new project. */
  showExisting?: boolean
}

/**
 * The three ways a video gets into a project: local upload, YouTube URL,
 * or a file already sitting in the channel's ``.tools/``.
 *
 * Shared by the projects screen (import → new project) and the "add
 * video" dialog (import → into this project) so the two can't drift.
 */
export function VideoImporter({ onVideo, usedTempPaths = [], showExisting = false }: VideoImporterProps) {
  const [tab, setTab] = useState<Tab>('upload')
  const [localError, setLocalError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [youtubeJobId, setYoutubeJobId] = useState<string | null>(null)
  const [uploading, setUploading] = useState<{ filename: string; startedAt: number } | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadForCutProgress | null>(null)
  // Upload that landed but isn't watchable yet — the backend is re-encoding
  // it to H.264. Held here until the job finishes so the editor never opens
  // on a video the browser would paint black.
  const [converting, setConverting] = useState<UploadedForCut | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const upload = useUploadForCut()
  const startYoutube = useStartYoutubeDownload()
  const ytJob = useJob(youtubeJobId)
  const txJob = useJob(converting?.transcode?.job_id ?? null)
  const imports = useImports()
  const deleteImport = useDeleteImport()
  const invalidateImports = useInvalidateImports()

  const used = useMemo(() => new Set(usedTempPaths), [usedTempPaths])

  // Hand the finished download over exactly once.
  useEffect(() => {
    if (!youtubeJobId) return
    const snap = readDownloadSnapshot(ytJob.data)
    if (!snap?.download_url) return
    const video = fromYoutube(snap)
    setYoutubeJobId(null)
    setYoutubeUrl('')
    startYoutube.reset()
    invalidateImports()
    if (video) onVideo(video)
    else setLocalError('O download terminou mas o arquivo não foi localizado no servidor.')
  }, [youtubeJobId, ytJob.data, startYoutube, invalidateImports, onVideo])

  // Hand the re-encoded video over exactly once, adopting the snapshot's
  // probe values: the conversion bakes the rotation flag into the pixels,
  // so a phone clip's width/height come back swapped.
  useEffect(() => {
    if (!converting) return
    const status = txJob.data?.status
    if (status === 'failed' || status === 'cancelled') {
      setConverting(null)
      setUploading(null)
      setLocalError(
        (txJob.data?.error?.['message'] as string | undefined) ??
          'A conversão do vídeo falhou. O arquivo continua no servidor, em "Já no servidor".',
      )
      return
    }
    const snap = readTranscodeSnapshot(txJob.data)
    if (!snap) return
    setConverting(null)
    setUploading(null)
    onVideo(
      fromUpload({
        ...converting,
        temp_path: snap.temp_path,
        video_url: snap.video_url,
        proxy_url: snap.proxy_url,
        duration: snap.duration ?? converting.duration,
        width: snap.width,
        height: snap.height,
        file_size: snap.file_size,
      }),
    )
  }, [converting, txJob.data, onVideo])

  const acceptFile = (file: File) => {
    setLocalError(null)
    if (!ACCEPTED_MIME.includes(file.type)) {
      setLocalError('Formato não suportado. Envie MP4, MOV, WEBM ou MKV.')
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setLocalError('Arquivo muito grande (máx 4 GB).')
      return
    }
    setUploading({ filename: file.name, startedAt: Date.now() })
    setUploadProgress(null)
    upload.mutate(
      { file, onProgress: setUploadProgress },
      {
        onSuccess: (data) => {
          invalidateImports()
          // Unplayable codec: keep the card up and wait for the conversion
          // job instead of handing the editor a black video.
          if (data.transcode) setConverting(data)
          else onVideo(fromUpload(data))
        },
        onSettled: (data) => {
          setUploadProgress(null)
          if (!data?.transcode) setUploading(null)
        },
      },
    )
  }

  // The conversion reports its own percentage through the job, so the card
  // gets a synthetic progress object rather than a second set of props.
  const convertingProgress: UploadForCutProgress = {
    phase: 'converting',
    loaded: 0,
    total: 0,
    percent: txJob.data?.progress ?? 0,
  }

  const trimmedYt = youtubeUrl.trim()
  const ytLooksValid = YOUTUBE_URL_RE.test(trimmedYt)
  const ytStatus = ytJob.data?.status
  const ytFailed = ytStatus === 'failed' || ytStatus === 'cancelled'
  const ytRunning = startYoutube.isPending || ytStatus === 'pending' || ytStatus === 'running'
  const ytProgress = ytJob.data?.progress ?? (startYoutube.isPending ? 2 : 0)
  const ytProgressMsg = ytJob.data?.message ?? (startYoutube.isPending ? 'Enviando...' : '')
  const ytErrorMsg = ytFailed
    ? ((ytJob.data?.error?.['message'] as string | undefined) ?? ytJob.data?.message)
    : null
  const uploadError = upload.isError ? errorMessage(upload.error, 'Falha ao enviar vídeo.') : null
  const ytStartError = startYoutube.isError
    ? errorMessage(startYoutube.error, 'Falha ao iniciar download.')
    : null

  const startYoutubeDownload = () => {
    if (!ytLooksValid || ytRunning) return
    setLocalError(null)
    startYoutube.mutate(
      { url: trimmedYt },
      {
        onSuccess: ({ job_id }) => {
          setYoutubeJobId(job_id)
        },
      },
    )
  }

  const removeImport = (item: ImportedItem) => {
    if (!window.confirm(`Excluir o arquivo "${item.filename}" do servidor?`)) return
    deleteImport.mutate(item.id)
  }

  return (
    <div className={styles.importer}>
      <div className={styles.importTabs}>
        <button
          type="button"
          className={cn(styles.importTab, tab === 'upload' && styles.importTabActive)}
          onClick={() => {
            setTab('upload')
          }}
        >
          <Upload aria-hidden />
          Upload
        </button>
        <button
          type="button"
          className={cn(styles.importTab, tab === 'youtube' && styles.importTabActive)}
          onClick={() => {
            setTab('youtube')
          }}
        >
          <Download aria-hidden />
          Baixar do YouTube
        </button>
        {showExisting ? (
          <button
            type="button"
            className={cn(styles.importTab, tab === 'existing' && styles.importTabActive)}
            onClick={() => {
              setTab('existing')
            }}
          >
            <Film aria-hidden />
            Já no servidor
          </button>
        ) : null}
      </div>

      {tab === 'upload' ? (
        <>
          {uploading ? (
            <UploadProgressCard
              filename={uploading.filename}
              progress={converting ? convertingProgress : uploadProgress}
              startedAt={uploading.startedAt}
              fromCodec={converting?.transcode?.from_codec ?? null}
            />
          ) : (
            <div
              className={cn(styles.dropzone, dragging && styles.dropzoneActive)}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => {
                setDragging(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                setDragging(false)
                const f = e.dataTransfer.files[0]
                if (f) acceptFile(f)
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              aria-label="Selecionar vídeo"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_MIME.join(',')}
                className={styles.hiddenInput}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) acceptFile(f)
                  e.target.value = '' // allow re-picking the same file
                }}
              />
              <div className={styles.dropIcon}>
                <Film aria-hidden />
              </div>
              <div className={styles.dropTitle}>Arraste um vídeo ou clique para escolher</div>
              <div className={styles.dropSub}>MP4, MOV, WEBM ou MKV · até 4 GB</div>
            </div>
          )}
          {localError ?? uploadError ? <p className={styles.error}>{localError ?? uploadError}</p> : null}
        </>
      ) : null}

      {tab === 'youtube' ? (
        <>
          <div className={styles.ytRow}>
            <input
              type="url"
              className={styles.ytInput}
              placeholder="https://www.youtube.com/watch?v=..."
              value={youtubeUrl}
              onChange={(e) => {
                setYoutubeUrl(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  startYoutubeDownload()
                }
              }}
              disabled={ytRunning}
              aria-label="URL do vídeo do YouTube"
              spellCheck={false}
            />
            <Button
              type="button"
              variant="primary"
              leadingIcon={ytRunning ? <Loader2 className={styles.spin} /> : <Download />}
              onClick={startYoutubeDownload}
              disabled={!ytLooksValid || ytRunning}
            >
              {ytRunning ? 'Baixando…' : 'Baixar'}
            </Button>
          </div>
          {youtubeUrl && !ytLooksValid && !ytRunning ? (
            <p className={styles.hint}>URL não parece ser um link do YouTube.</p>
          ) : null}
          {ytRunning ? (
            <div className={styles.progressCard}>
              <div className={styles.progressHeader}>
                <Youtube className={styles.progressIcon} aria-hidden />
                <span className={styles.progressMsg}>{ytProgressMsg || 'Preparando...'}</span>
                <span className={styles.progressPct}>{ytProgress}%</span>
              </div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${String(ytProgress)}%` }} />
              </div>
            </div>
          ) : ytFailed ? (
            <p className={styles.error}>{ytErrorMsg ?? 'Falha ao baixar o vídeo.'}</p>
          ) : ytStartError ? (
            <p className={styles.error}>{ytStartError}</p>
          ) : localError ? (
            <p className={styles.error}>{localError}</p>
          ) : null}
        </>
      ) : null}

      {tab === 'existing' ? (
        <div className={styles.existingList}>
          {imports.isPending ? (
            <p className={styles.hint}>Carregando…</p>
          ) : imports.data && imports.data.length > 0 ? (
            <ul className={styles.importsUl}>
              {imports.data.map((item) => {
                const already = used.has(item.temp_path)
                return (
                  <li key={item.id} className={styles.importsItem}>
                    <button
                      type="button"
                      className={styles.importsOpen}
                      disabled={already}
                      onClick={() => {
                        onVideo(fromImport(item))
                      }}
                      aria-label={`Adicionar ${item.filename}`}
                    >
                      <span className={styles.importsIcon} aria-hidden>
                        {already ? <Check /> : item.kind === 'youtube' ? <Youtube /> : <Film />}
                      </span>
                      <span className={styles.importsBody}>
                        <span className={styles.importsName}>{item.filename}</span>
                        <span className={styles.importsMeta}>
                          {already
                            ? 'já está no projeto'
                            : [formatBytes(item.size_bytes), relativeTime(item.modified_at)]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className={styles.importsDel}
                      onClick={() => {
                        removeImport(item)
                      }}
                      disabled={deleteImport.isPending || already}
                      aria-label={`Excluir ${item.filename}`}
                      title={
                        already
                          ? 'Em uso neste projeto'
                          : 'Excluir o arquivo do servidor (projetos que usam ele ficam sem a mídia)'
                      }
                    >
                      <Trash2 aria-hidden />
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className={styles.hint}>Nenhum vídeo no servidor ainda.</p>
          )}
        </div>
      ) : null}
    </div>
  )
}
